import { useContext, useEffect, useMemo, useState } from 'react';
import { SectionsContext, useModal } from '../contexts';
import { Workflow, WorkflowFolder } from "../interfaces";
import { PlusIcon, SearchIcon, TrashIcon, ViewGridIcon, ViewListIcon, XIcon } from '@heroicons/react/outline';
import { getData, postData } from '../backend';
import useUser from '../hooks/useUser';
import TextInputField from './TextInputField';
import WorkflowSelectModal from './WorkflowSelectModal';
import WorkflowListCard from './WorkflowListCard';
import WorkflowListRow from './WorkflowListRow';
import Paginator from './Paginator';
import useWorkflows from '../hooks/useWorkflows';
import useDebounce from '../hooks/useDebounce';
import WorkflowModalDeleteBatch from './WorkflowModalDeleteBatch';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import WorkflowFolderRow from './WorkflowFolderRow';
import FolderModal from './FolderModal';

interface Props {
  createNewWorkflow: (stage: "SOURCE" | "DOCUMENT", blockType: string, file?: any, isPdf?: boolean, options?: Record<string, any>) => void;
}

function WorkflowList(props: Props) {
  const { user, hasPermission } = useUser();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const { workflow, updateWorkflowData } = useContext(SectionsContext);

  const [selectedWorkflows, setSelectedWorkflows] = useState<Array<string>>([]);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);

  const {
    workflowFilter,
    workflowListView,
    workflowSearch,
    workflowShowSearch,
  } = workflow;

  const debouncedSearch = useDebounce(workflowSearch, 200);

  const { data, mutate: mutateWorkflows, isValidating } = useWorkflows({ workflowType: workflowFilter, search: workflowShowSearch ? debouncedSearch : "", folder: currentFolder || (workflowFilter === "TEAM" ? "__root" : undefined) }, currentPageNumber);
  const { results: filteredWorkflows, numPages, total } = data ?? { results: undefined, numPages: 1, total: 0 };

  const { data: folders, mutate: mutateFolders } = useSWR<Array<WorkflowFolder>>("/workflows/folders/", getData, { fallbackData: [] });

  useEffect(() => {
    mutateFolders(); // revalidate folders when the workflow list is updated
  }, [filteredWorkflows, mutateFolders]);

  const folderMap = useMemo(() => {
    const map: Record<string, WorkflowFolder> = {};
    folders?.forEach(folder => {
      map[folder.id] = folder;
    });
    return map;
  }, [folders]);

  const folderPath = useMemo(() => {
    if (currentFolder === null)
      return null;

    const path: Array<WorkflowFolder> = [];
    let folder = folderMap[currentFolder];

    path.push(folder);
    while (folder.parentFolder) {
      folder = folderMap[folder.parentFolder];
      path.push(folder);
    }

    return [...path.reverse()];
  }, [folderMap, currentFolder]);

  useEffect(() => {
    if (workflowShowSearch)
      document.getElementById("search-field")?.focus();
  }, [workflowShowSearch])

  useEffect(() => {
    if (workflowListView !== "ROWS")
      setSelectedWorkflows([]);
  }, [workflowListView]);

  const enabledPrevious = currentPageNumber > 1;
  const enabledNext = currentPageNumber < numPages;

  function onPageChange(newPage: number) {
    setCurrentPageNumber(newPage);
  }

  function onTypeChange(filterType: "ALL" | "PERSONAL" | "TEAM") {
    setCurrentPageNumber(1);
    setCurrentFolder(null);
    updateWorkflowData("workflowFilter", filterType);
  }

  const onSearchChange = (newValue: string) => {
    setCurrentPageNumber(1);
    updateWorkflowData('workflowSearch', newValue)
  }

  function handleAddWorkflow() {
    if (!hasPermission(user?.activeTeam, "workflows.create"))
      return;

    openModal(<WorkflowSelectModal title={t("New Workflow")} subtitle={t("Select a source or template document to start building a workflow")} showDocuments showSources onSelection={props.createNewWorkflow} />);
  }

  function handleSelectWorkflow(workflowId: string) {
    if (selectedWorkflows.includes(workflowId))
      setSelectedWorkflows(selectedWorkflows.filter(id => id !== workflowId));
    else
      setSelectedWorkflows([...selectedWorkflows, workflowId]);
  }

  function handleAddFolder(folder: WorkflowFolder) {
    closeModal();

    mutateFolders((prev) => [folder, ...(prev ?? [])], { revalidate: false });

    postData("/workflows/folders/", folder)
      .then((folder) => mutateFolders(prev => [folder, ...(prev ?? [])]))
      .catch((e) => console.error(e));
  }

  const filteredFolders = folders?.filter(f => f.parentFolder === currentFolder && f.name.toLowerCase().includes(workflowSearch.toLowerCase()));
  const noResultes = (workflowFilter !== "ALL" && !isValidating && (filteredWorkflows ?? []).length === 0 && (filteredFolders ?? []).length === 0);

  if (user === undefined)
    return <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24 m-auto" alt="Loading Logo" />;

  return (
    <div className="flex flex-col border border-gray-200 rounded-lg bg-white p-4 w-full h-full">
      <div className='flex items-center justify-between px-4 py-2 border-b border-gray-400 flex-shrink-0 h-14'>
        <div className='flex items-baseline overflow-auto gap-4'>
          <button
            className={`font-semibold font-gilroy ${(workflowFilter === "ALL" && !currentFolder) ? "text-gray-600" : "text-gray-400"} hover:text-gray-600 cursor-pointer select-none whitespace-nowrap`}
            onClick={() => { onTypeChange("ALL"); setCurrentFolder(null) }}
          >
            {t("All")} {((total ?? 0) > 0) && `(${total})`}
          </button>
          {user?.activeTeam &&
            <>
              <span className="font-semibold font-gilroy text-gray-400">|</span>
              <button
                className={`font-semibold font-gilroy ${workflowFilter === "PERSONAL" ? "text-gray-600" : "text-gray-400"} hover:text-gray-600 cursor-pointer select-none whitespace-nowrap`}
                onClick={() => onTypeChange("PERSONAL")}
              >
                {t("Personal")}
              </button>
              <span className="font-semibold font-gilroy text-gray-400">|</span>
              <button
                className={`font-semibold font-gilroy ${(workflowFilter === "TEAM" && !currentFolder) ? "text-gray-600" : "text-gray-400"} hover:text-gray-600 cursor-pointer select-none whitespace-nowrap`}
                onClick={() => onTypeChange("TEAM")}>
                {t("Team")} ({user.activeTeam?.name})
              </button>
              {folderPath?.map(folder =>
                <div key={folder.id} className='flex items-center gap-4'>
                  <span className="font-semibold font-gilroy text-gray-400">&gt;</span>
                  <div className={`font-semibold font-gilroy cursor-pointer hover:underline hover:text-gray-600 ${currentFolder === folder.id ? "text-gray-600" : "text-gray-400"}`}>{folder.name}</div>
                </div>
              )}
              {(workflowFilter === "TEAM" && hasPermission(user?.activeTeam, "workflows.edit") && currentFolder === null) &&
                <button onClick={() => openModal(<FolderModal updateFolder={handleAddFolder} acceptText="Create" titleText="Create New Folder" />)}>
                  <div className={`font-semibold font-gilroy cursor-pointer hover:underline hover:text-gray-600 text-gray-400 text-sm -ml-2`}>Create Folder</div>
                </button>
              }
            </>
          }
        </div>
        {(selectedWorkflows.length > 0 && workflowListView === "ROWS") &&
          <div className='flex items-center gap-4 text-sm bg-blue-50 rounded-full h-full px-4 text-gray-600'>
            <div className='flex gap-2 text-sm font-semibold'>
              {selectedWorkflows.length} {t("selected")}
            </div>
            |
            <button className='flex items-center gap-1 cursor-pointer hover:text-red font-semibold' onClick={() => openModal(<WorkflowModalDeleteBatch workflowIds={selectedWorkflows} onCompletion={() => { mutateWorkflows(); setSelectedWorkflows([]) }} />)}>
              <TrashIcon className='w-4 h-4' />
              {t("Delete")}
            </button>
            {/* TODO: Add batch sharing */}
            {/* {user?.activeTeam &&
              <button className='flex items-center gap-1 cursor-pointer hover:text-blue font-semibold' onClick={() => openModal(<WorkflowModalShareBatch workflowIds={selectedWorkflows} onCompletion={() => { mutateWorkflows(); setSelectedWorkflows([]) }} />)}>
                <UserGroupIcon className='w-4 h-4' onClick={() => setSelectedWorkflows([])} />
                Share
              </button>
            } */}
            <button className='flex items-center gap-1 cursor-pointer transition-transform hover:scale-125 text-gray-600 font-semibold p-2 -m-2'>
              <XIcon className='w-4 h-4' onClick={() => setSelectedWorkflows([])} />
            </button>
          </div>
        }

        <div className='flex items-center gap-4'>
          <div className='flex items-center'>
            <TextInputField
              id="search-field"
              className={`transition-all ${workflowShowSearch ? "w-full lg:w-80 opacity-100" : "w-10 opacity-0 pointer-events-none"}`}
              value={workflowSearch}
              onChange={onSearchChange}
              placeholder="Search Workflows"
              tabIndex={workflowShowSearch ? 0 : -1}
            />
            <button
              className={`${workflowShowSearch ? "z-10" : "z-0"}`}
              onClick={() => updateWorkflowData('workflowShowSearch', !workflowShowSearch)}
            >
              <SearchIcon className="w-6 h-6 cursor-pointer -ml-8 text-gray-400 hover:text-gray-500" />
            </button>
          </div>
          {(workflowListView === "ROWS") &&
            <button onClick={() => updateWorkflowData("workflowListView", "CARDS")}>
              <ViewGridIcon className="w-6 h-6 ml-2 mr-2 cursor-pointer text-gray-400 hover:text-blue" />
            </button>
          }
          {workflowListView === "CARDS" &&
            <button onClick={() => updateWorkflowData("workflowListView", "ROWS")}>
              <ViewListIcon className="w-6 h-6 ml-2 mr-2 cursor-pointer text-gray-400 hover:text-blue" />
            </button>
          }
          <button className='btn btn-blue text-sm py-1 px-4' onClick={handleAddWorkflow} disabled={!hasPermission(user?.activeTeam, "workflows.create")}>
            <PlusIcon className='w-4 h-4 mr-2' /> {t("Add Workflow")}
          </button>
        </div>
      </div>

      {(noResultes && total !== 0 && debouncedSearch === "") && (
        <div className='flex flex-col w-full h-full justify-center items-center font-gilroy font-semibold text-gray-400'>
          No Results
        </div>
      )}

      {(total === 0 && debouncedSearch === "" && !isValidating && workflowFilter === "ALL") &&
        <div className='flex flex-col items-center mb-4 w-full overflow-auto'>
          <div className="flex flex-col gap-2 justify-center items-center w-full mt-10 mb-14">
            <div className="font-gilroy font-semibold text-lg">{t("Automate a document workflow")}</div>
            <button id="workflow-get-started-button" className="btn btn-blue" onClick={handleAddWorkflow}>
              {t("Start From Scratch")}
            </button>
          </div>
        </div>
      }

      {(isValidating && !filteredWorkflows) && <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24 m-auto" alt="Loading Logo" />}

      {filteredWorkflows && workflowListView === "ROWS" &&
        <div className="flex flex-col px-8 -mx-4 py-0 overflow-y-auto" style={{ height: `calc(100vh - 144px` }}>
          {workflowFilter === "TEAM" &&
            <>
              {filteredFolders?.map((folder: WorkflowFolder) =>
                <WorkflowFolderRow key={folder.id} folder={folder} onChange={() => mutateWorkflows()} mutateFolders={mutateFolders} onClick={() => { setCurrentFolder(folder.id); updateWorkflowData("workflowSearch", "") }} />
              )}
            </>
          }

          {filteredWorkflows.map((workflow: Workflow) =>
            <WorkflowListRow key={workflow.id} workflow={workflow} mutateWorkflowList={mutateWorkflows} onSelect={handleSelectWorkflow} selected={selectedWorkflows.includes(workflow.id)} disableOptions={selectedWorkflows.length > 0} />
          )}
          {(filteredWorkflows.length === 0 && debouncedSearch !== "") &&
            <div className="mt-20 mx-auto font-gilroy text-gray-600">
              {!!debouncedSearch ? `${t("No results found for")} "${debouncedSearch}"` : t("No results")}
            </div>
          }
        </div>
      }

      {filteredWorkflows && workflowListView === "CARDS" &&
        <div className="grid grid-cols-3 lg:grid-cols-4 grid-rows-none gap-8 row p-4 overflow-y-auto font-gilroy w-full">
          {workflowFilter === "TEAM" &&
            <div className="flex col-span-3 lg:col-span-4 flex-col -mt-4 -mb-8">
              {filteredFolders?.map((folder: WorkflowFolder) =>
                <WorkflowFolderRow key={folder.id} folder={folder} onChange={() => mutateWorkflows()} mutateFolders={mutateFolders} onClick={() => { setCurrentFolder(folder.id); updateWorkflowData("workflowSearch", "") }} />
              )}
            </div>
          }
          {filteredWorkflows.map((workflow: Workflow) =>
            <WorkflowListCard key={workflow.id} workflow={workflow} mutateWorkflowList={mutateWorkflows} />
          )}
          {(filteredWorkflows.length === 0 && debouncedSearch !== "") &&
            <div className="mt-20 mx-auto font-gilroy text-gray-600 col-span-4">
              {!!debouncedSearch ? `No results found for "${debouncedSearch}"` : "No results"}
            </div>
          }
        </div>
      }

      <div className='mt-4' />

      <Paginator
        enabledNext={enabledNext}
        enabledPrevious={enabledPrevious}
        onPageChange={onPageChange}
        pageNumber={currentPageNumber}
        numPages={numPages}
        className="mt-auto"
      />
    </div>
  );
}

export default WorkflowList;
